import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { buildUrlWithParams } from '../../utils.js';
import CardCarousel from '../components/CardCarousel';
import useStablePanelHeight from '../hooks/useStablePanelHeight';
import { backgroundOverlay, backgroundStyle, backgroundValue, gradientStyle } from '../utils/presentation';

const cardLink = (card, className = '') => <Link key={card.id || card.cardid} className={className} to={buildUrlWithParams({ ...Object.fromEntries(new URLSearchParams(window.location.search)), selectedCard: card.id })} preventScrollReset><img src={`https://www.hearthcards.net/cards/${card.cardid}.webp`} alt={card.cardname || 'Card'} /></Link>;

export default function ClassFeaturesModule({ module }) {
  const { features = [] } = module;
  const data = module.data || {};
  const [activeIndex, setActiveIndex] = useState(0);
  const tabsRef = useRef(null);
  const tabListRef = useRef(null);
  const indicatorRef = useRef(null);
  const stablePanel = useStablePanelHeight(features.map((entry) => entry.id).join('|'));
  useEffect(() => { if (activeIndex >= features.length) setActiveIndex(0); }, [activeIndex, features.length]);
  const updateIndicator = useCallback(() => {
    const list = tabListRef.current;
    const indicator = indicatorRef.current;
    const tab = list?.querySelectorAll('button[role="tab"]')?.[activeIndex];
    if (!tab || !indicator) return;
    indicator.style.transform = `translateX(${tab.offsetLeft}px)`;
    indicator.style.width = `${tab.offsetWidth}px`;
    const viewport = tabsRef.current;
    if (viewport) viewport.scrollTo({ left: tab.offsetLeft - (viewport.clientWidth - tab.offsetWidth) / 2, behavior: 'smooth' });
  }, [activeIndex]);
  useEffect(() => {
    const frame = window.requestAnimationFrame(updateIndicator);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateIndicator);
    if (tabListRef.current) observer?.observe(tabListRef.current);
    window.addEventListener('resize', updateIndicator);
    return () => { window.cancelAnimationFrame(frame); observer?.disconnect(); window.removeEventListener('resize', updateIndicator); };
  }, [features.length, updateIndicator]);
  const selectTab = (index) => setActiveIndex(index);
  if (!features.length) return null;
  const feature = features[activeIndex];
  const item = feature?.data || {};
  const background = backgroundValue(item);
  const heroPower = item.heropower?.[0];
  const cards = item.cards || [];
  return <section className="set-class-features-modern" style={{ backgroundColor: item.backgroundColor || data.backgroundColor || '#000926', '--class-feature-accent': data.accentColor || '#fcd144' }}>
    {background && <div className="set-class-feature-background" style={backgroundStyle(background, { size: item.backgroundSize || 'cover', position: item.backgroundPosition || 'center', tileSize: item.backgroundTileSize })} />}
    <div className="set-presentation-shade" style={{ '--section-overlay': backgroundOverlay(item, data.backgroundDarkness ?? 45) }} />
    <div className="set-presentation-gradient" style={gradientStyle(data, { direction: 'to right', color: '#000926', strength: 90 })} />
    <div className="container set-wide-section">
      {(data.title || data.intro) && <header className="set-section-heading">{data.title && <h2>{data.title}</h2>}{data.intro && <p>{data.intro}</p>}</header>}
      <div className="set-class-feature-tabs" ref={tabsRef}><div className="set-class-feature-tab-list" ref={tabListRef} role="tablist">{features.map((entry, index) => <button key={entry.id || index} type="button" role="tab" aria-selected={activeIndex === index} className={activeIndex === index ? 'set-is-active' : ''} onClick={() => selectTab(index)}>{entry.data?.title || `Feature ${index + 1}`}</button>)}<span className="set-class-tab-indicator" ref={indicatorRef} /></div></div>
      <article className="set-class-feature-content" ref={stablePanel.ref} style={stablePanel.minHeight ? { minHeight: `${stablePanel.minHeight}px` } : undefined}>
        {item.title && <h3>{item.title}</h3>}
        {item.description && <p>{item.description}</p>}
        {feature.type === 'heropower' && heroPower && <div className="set-class-hero-power"><div>{cardLink(heroPower, 'set-hero-power-card')}</div><div>{item.hptitle && <h3>{item.hptitle}</h3>}{item.subtitle && <p>{item.subtitle}</p>}</div></div>}
        {cards.length > 0 && <CardCarousel className="set-class-card-carousel" variableWidth cardWidth={220} mobileCenter desktopArrowThreshold={5}>{cards.map((card) => cardLink(card))}</CardCarousel>}
      </article>
    </div>
  </section>;
}
